import React from "react";
import { useTranslation } from "react-i18next";
import { CarWithContextModel } from "../../../infrastructure/slices/requestQuickAddSliceModel";
import DatePicker, { DatePickerProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type RequestType = "deliver" | "pickup";

interface NewRequestFormProps {
  requestType: RequestType | null;
  workOrderNumber: string;
  pickupDeliveryDate: string;
  tireSetType: string;
  selectedCar: CarWithContextModel | null;
  onWorkOrderNumberChange: (value: string) => void;
  onPickupDeliveryDateChange: (value: string) => void;
  onTireSetTypeChange: (value: string) => void;
  onNewRequestSubmit: () => void;
  datePickerOptions?: Partial<DatePickerProps>;
}

export const NewRequestForm: React.FC<NewRequestFormProps> = ({
  requestType,
  workOrderNumber,
  pickupDeliveryDate,
  tireSetType,
  selectedCar,
  onWorkOrderNumberChange,
  onPickupDeliveryDateChange,
  onTireSetTypeChange,
  onNewRequestSubmit,
  datePickerOptions,
}) => {
  const { t } = useTranslation("common");

  // Only extract allowed single-date props
  const filterDate = datePickerOptions?.filterDate;
  const monthsShown = datePickerOptions?.monthsShown;
  const minDate = datePickerOptions?.minDate;
  const maxDate = datePickerOptions?.maxDate;
  const highlightDates = datePickerOptions?.highlightDates;
  const excludeDates = datePickerOptions?.excludeDates;

  if (!requestType) return null;

  return (
    <div className="request-form mt-4">
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="pickupDeliveryDate" className="form-label">
            {t("request.date_label", {
              type:
                requestType === "deliver"
                  ? t("delivered").toLowerCase()
                  : t("picked up").toLowerCase(),
            })}
          </label>
          <DatePicker
            id="pickupDeliveryDate"
            selected={
              pickupDeliveryDate ? new Date(pickupDeliveryDate) : minDate
            }
            onChange={(date: Date | null) => {
              if (date) {
                // Format date as YYYY-MM-DD using local time
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                onPickupDeliveryDateChange(`${year}-${month}-${day}`);
              } else {
                onPickupDeliveryDateChange("");
              }
            }}
            className="form-control"
            dateFormat="dd-MM-yyyy"
            showWeekNumbers
            showIcon
            calendarStartDay={1}
            locale="nl"
            filterDate={filterDate}
            monthsShown={monthsShown}
            minDate={minDate}
            maxDate={maxDate}
            excludeDates={excludeDates}
            highlightDates={highlightDates}
            wrapperClassName={"form-control"}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor="workOrderNumber" className="form-label">
            {t("request.wo_number_label")}
          </label>
          <input
            type="text"
            className="form-control"
            id="workOrderNumber"
            value={workOrderNumber}
            onChange={(e) => onWorkOrderNumberChange(e.target.value)}
            placeholder={t("Enter work order number")}
          />
        </div>
        {!selectedCar && (
          <div className="col-md-6">
            <label htmlFor="tireSetType" className="form-label">
              {t("Tire Set Type")}
            </label>
            <select
              className="form-select"
              id="tireSetType"
              value={tireSetType}
              onChange={(e) => onTireSetTypeChange(e.target.value)}
            >
              <option value="">{t("Select tire set type...")}</option>
              <option value="summer">{t("Summer")}</option>
              <option value="winter">{t("Winter")}</option>
              <option value="all_season">{t("All season")}</option>
            </select>
          </div>
        )}
      </div>
      <div className="mt-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onNewRequestSubmit}
        >
          {requestType === "deliver"
            ? t("Create delivery request")
            : t("Create pickup request")}
        </button>
      </div>
    </div>
  );
};
